import { useFolderStructure } from '@src/stores-v2/StoreContext';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import Modal from '../../common/Modal';
import {
  DialogCard,
  DialogFooter,
  DialogHeader,
  DialogSubtitle,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from './folderDialogStyles';
import FolderIcon from './FolderIcon';
import { selectedRowIndicator } from './selectionStyles';

import type { Folder } from '@src/types.ts/story';
import type { FC } from 'react';

type Props = {
  storyIds: string[];
  /** Title summary text e.g. story title (single) — optional. */
  title?: string;
  onSubmit: (folderId: string | null) => Promise<void> | void;
  onClose: () => void;
};

type Row = { folder: Folder | null; depth: number };

const MoveStoryDialog: FC<Props> = observer(
  ({ storyIds, title, onSubmit, onClose }) => {
    const store = useFolderStructure();

    const rows = useMemo<Row[]>(() => {
      const out: Row[] = [{ folder: null, depth: 0 }];
      const byParent = store.foldersByParent;
      const walk = (parentId: string | null, depth: number) => {
        const children = byParent.get(parentId) ?? [];
        for (const f of children) {
          out.push({ folder: f, depth });
          walk(f.id, depth + 1);
        }
      };
      walk(null, 1);
      return out;
    }, [store.foldersByParent]);

    // Preselect: single-story = its current folder; bulk = no preselect.
    const initialSelection = useMemo<string | null | undefined>(() => {
      if (storyIds.length !== 1) return undefined;
      return store.folderIdForStory(storyIds[0]);
    }, [storyIds, store]);

    const [selected, setSelected] = useState<string | null | undefined>(
      initialSelection,
    );
    const [busy, setBusy] = useState(false);

    const isNoOp = useMemo(() => {
      if (selected === undefined) return true;
      // Disable Move if every selected story already lives in `selected`.
      return storyIds.every(id => store.folderIdForStory(id) === selected);
    }, [selected, storyIds, store]);

    const submit = async () => {
      if (selected === undefined) return;
      setBusy(true);
      try {
        await onSubmit(selected);
      } finally {
        setBusy(false);
      }
    };

    const isSingle = storyIds.length === 1;
    const headingText = isSingle
      ? 'Move story to…'
      : `Move ${storyIds.length} stories to…`;

    return (
      <Modal isOpen closeModal={busy ? () => undefined : onClose}>
        <DialogCard $size="lg" data-testid="move-story-dialog">
          <DialogHeader>
            <DialogTitle>{headingText}</DialogTitle>
            {isSingle && title && (
              <DialogSubtitle title={title}>{`“${title}”`}</DialogSubtitle>
            )}
          </DialogHeader>
          <List>
            {rows.map(row => {
              const id = row.folder?.id ?? null;
              const isSelected = selected === id;
              return (
                <RowButton
                  type="button"
                  key={row.folder?.id ?? '__root__'}
                  data-selected={isSelected}
                  data-testid={
                    row.folder
                      ? `move-target-${row.folder.id}`
                      : 'move-target-root'
                  }
                  style={{ paddingLeft: `calc(${row.depth} * 20px + 12px)` }}
                  onClick={() => setSelected(id)}
                >
                  <FolderIcon />
                  <Name>{row.folder ? row.folder.name : 'Workspace'}</Name>
                </RowButton>
              );
            })}
          </List>
          <DialogFooter>
            <SecondaryButton
              type="button"
              disabled={busy}
              onClick={onClose}
              data-testid="move-story-cancel"
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="button"
              disabled={busy || isNoOp}
              onClick={() => void submit()}
              data-testid="move-story-confirm"
            >
              Move
            </PrimaryButton>
          </DialogFooter>
        </DialogCard>
      </Modal>
    );
  },
);

export default MoveStoryDialog;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
  max-height: 360px;
  padding: 4px;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  background: var(--background);
`;

const RowButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  color: var(--foreground);
  transition: background 120ms ease;

  &:hover {
    background: var(--surface);
  }

  &[data-selected='true'] {
    ${selectedRowIndicator}
    color: var(--text-primary);
  }
`;

const Name = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  color: inherit;
`;
